<template>
  <div class="order-container">
    <!-- 日期范围选择器和导出按钮 -->
    <div class="date-picker-section">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        @change="handleDateRangeChange"
        :disabled="loading || isExporting"
        value-format="yyyy-MM-dd">
      </el-date-picker>
      
      <el-button 
        type="success" 
        icon="el-icon-download" 
        @click="handleExport"
        :loading="isExporting"
        class="export-btn">
        导出
      </el-button>
    </div>
    
    <!-- 带选择框的订单表格 -->
    <div class="order-list-section">
      <el-table
        ref="multipleTable"
        :data="orderList"
        border
        style="width: 100%; margin-top: 20px;"
        v-loading="loading"
        @row-click="handleRowClick"
        @selection-change="handleSelectionChange"
        row-class-name="clickable-row"
        tooltip-effect="dark">
        
        <!-- 选择框列 -->
        <el-table-column
          type="selection"
          width="55">
        </el-table-column>
        
        <!-- 序号列 -->
        <el-table-column
          type="index"
          width="50"
          label="序号">
        </el-table-column>
        
        <!-- 司机姓名列 -->
        <el-table-column
          prop="driverName"
          label="司机姓名"
          width="120">
        </el-table-column>
        
        <!-- 地址列 -->
        <el-table-column
          prop="addr"
          label="地址"
          show-overflow-tooltip>
        </el-table-column>
        
        <!-- 订单日期列 -->
        <el-table-column
          prop="orderDate"
          label="订单日期"
          width="120">
          <template slot-scope="scope">{{ scope.row.orderDate }}</template>
        </el-table-column>
      </el-table>
      
      <!-- 空状态提示 -->
      <div v-if="orderList.length === 0 && !loading" class="empty-state">
        暂无订单数据
      </div>
    </div>
  </div>
</template>

<script>
import adminrequest from '@/utils/adminrequest'
import { DatePicker, Table, TableColumn, Message, Button } from 'element-ui';

export default {
  components: {
    ElDatePicker: DatePicker,
    ElTable: Table,
    ElTableColumn: TableColumn,
    ElButton: Button
  },
  data() {
    return {
      userId: '',
      dateRange: [], // 存储日期范围 ['startDate', 'endDate']
      orderList: [], // 订单列表数据
      loading: false,
      isExporting: false, // 导出加载状态
      multipleSelection: [] // 存储选中的订单数据
    };
  },
  created() {
    // 获取URL中的用户ID参数
    this.userId = this.$route.query.userId || '';
    
    if (!this.userId) {
      Message.warning('未获取到用户ID');
      return;
    }
    
    // 初始化默认日期范围为近30天
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    this.dateRange = [this.formatToDateString(startDate), this.formatToDateString(endDate)];
    
    // 页面加载时查询订单数据
    this.fetchOrderList();
  },
  methods: {
    /**
     * 格式化日期为yyyy-MM-dd格式
     */
    formatToDateString(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    /**
     * 日期范围变化时触发查询
     */
    handleDateRangeChange(range) {
      if (range && range.length === 2) {
        const formattedStart = this.formatToDateString(range[0]);
        const formattedEnd = this.formatToDateString(range[1]);
        this.dateRange = [formattedStart, formattedEnd];
        this.fetchOrderList();
      } else {
        Message.warning('请选择完整的日期范围');
      }
    },
    
    /**
     * 表格行点击事件 - 跳转至订单详情
     */
    handleRowClick(row) {
      if (row && row.orderId) {
        this.$router.push({
          path: '/OrderDetail',
          query: { orderId: row.orderId }
        });
      } else {
        Message.warning('订单信息不完整，无法查看详情');
      }
    },
    
    /**
     * 选择框变化事件 - 记录选中的订单
     */
    handleSelectionChange(val) {
      this.multipleSelection = val;
      console.log('当前选中的订单:', this.multipleSelection);
    },
    
    /**
     * 请求订单列表数据
     */
    async fetchOrderList() {
      // 验证用户ID
      if (!this.userId) {
        Message.error('用户ID不存在，无法查询订单');
        return;
      }
      
      // 验证日期范围
      if (!this.dateRange || this.dateRange.length !== 2 || !this.dateRange[0] || !this.dateRange[1]) {
        Message.warning('请选择完整的日期范围');
        return;
      }
      
      // 显示加载状态
      this.loading = true;
      
      try {
        const [startDate, endDate] = this.dateRange;
        const options = {
          method: 'GET',
          url: '/admin/manage/order/getOrderList',
          params: {
            driverId: this.userId,
            startDate: startDate,
            endDate: endDate
          }
        };
        
        // 发送请求
        const response = await adminrequest.request(options);
        
        // 处理响应
        if (response.data.code === 200) {
          this.orderList = response.data.data || [];
          if (this.orderList.length === 0) {
            Message.info('该日期范围内没有找到订单数据');
          }
        } else {
          Message.error(`查询失败: ${response.data.message || '未知错误'}`);
          this.orderList = [];
        }
      } catch (error) {
        console.error('请求订单列表失败:', error);
        Message.error(`网络错误: ${error.message || '请检查网络连接'}`);
        this.orderList = [];
      } finally {
        // 隐藏加载状态
        this.loading = false;
      }
    },
    
    /**
     * 导出选中的订单为Excel
     */
    handleExport() {
      if (this.multipleSelection.length === 0) {
        this.$message.warning('请先选择要导出的记录');
        return;
      }
      
      // 提取选中项的orderId组成数组
      const orderIds = this.multipleSelection.map(item => item.orderId);
      console.log('导出的orderId列表:', orderIds);
      
      this.isExporting = true;
      
      // 准备导出请求参数，添加参数序列化配置
      const options = {
        method: 'GET',
        url: '/admin/manage/order/getExcel',
        params: { 
          orderIds: orderIds  // 传递订单ID数组
        },
        // 自定义参数序列化，将数组转为orderIds=10&orderIds=22格式
        paramsSerializer: params => {
          return Object.entries(params)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                // 数组参数：重复参数名
                return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
              }
              // 非数组参数：正常拼接
              return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
            })
            .join('&');
        },
        responseType: 'blob'  // 接收二进制文件
      };
      
      adminrequest.request(options)
        .then(response => {
          this.isExporting = false;
          
          // 处理Excel文件下载
          const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          
          // 从响应头获取文件名
          let fileName = '订单记录.xlsx';
          const contentDisposition = response.headers['content-disposition'];
          if (contentDisposition) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
            if (matches && matches[1]) {
              fileName = decodeURIComponent(matches[1].replace(/['"]/g, ''));
            }
          }
          
          // 创建下载链接并触发下载
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          
          // 清理资源
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          this.$message.success('Excel文件导出成功');
        })
        .catch(error => {
          this.isExporting = false;
          console.error('导出失败:', error);
          
          // 错误提示
          if (error.response && error.response.status === 400) {
            this.$message.error('导出失败：参数错误，请检查选中的记录');
          } else if (error.response && error.response.status === 401) {
            this.$message.error('导出失败：未授权，请重新登录');
          } else if (error.response && error.response.status === 404) {
            this.$message.error('导出失败：接口不存在，请检查URL是否正确');
          } else {
            this.$message.error('导出失败，请稍后重试');
          }
        });
    }
  }
}
</script>

<style scoped>
.order-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
}

.date-picker-section {
  margin: 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.el-date-picker {
  width: 380px;
}

.export-btn {
  margin-left: auto;
}

.order-list-section {
  margin-top: 10px;
}

/* 表格行样式 */
.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background-color: #f5f7fa;
}

/* 空状态样式 */
.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin-top: 20px;
}

/* 隐藏默认空表格提示，使用自定义空状态 */
::v-deep .el-table__empty-block {
  display: none;
}
</style>
